
















































































import Vue, { PropType, VueConstructor } from "vue";
import VaButton from "@/components/atoms/VaButton.vue";
import VaSwitch from "@/components/atoms/VaSwitch.vue";
import wizardStepsMixin, {
  WizardMixin,
} from "@/components/mixins/wizardStepsMixin";
import cloneDeep from "lodash/clone";
import { WizardConfiguration } from "@/models/wizardConfiguration";
import validationMixin from "@/components/mixins/validationMixin";
import {
  ExternalProvider,
  FeaturePayload,
  PodExternalAccountConfiguration,
  ProviderAccount,
  ProviderAccountItem,
} from "@/models/providerAccount";
import * as externalProvidersService from "@/services/externalProvidersService";
import SafariLoginDialog from "../safariLoginDialog.vue";

export default (Vue as VueConstructor<WizardMixin>).extend({
  name: "StepMeeting",
  mixins: [wizardStepsMixin, validationMixin],
  data() {
    return {
      externalAccounts:
        (this.wizardConfiguration
          ?.externalAccounts as PodExternalAccountConfiguration) || null,
      pendingLoginUrl: null as string | null,
      pendingProvider: null as ExternalProvider | null,
      switchResetKey: 0,
    };
  },
  props: {
    activeStep: {
      type: Number,
      required: false,
      default: 0,
    },
    allowedProviders: {
      type: Array as PropType<ExternalProvider[]>,
      default: () => [],
    },
  },
  components: { VaButton, VaSwitch, SafariLoginDialog },
  computed: {
    isVisible(): boolean {
      return this.activeStep === this.index + 1;
    },
    providers(): ProviderAccountItem[] {
      if (this.allowedProviders && this.allowedProviders.length > 0) {
        // New format: backend decides which providers to show
        return externalProvidersService.supportedAccounts.filter((a) =>
          this.allowedProviders.includes(a.provider as ExternalProvider)
        );
      }
      // Legacy: show providers that support JoinMeeting, excluding capability-gated
      // ones — legacy devices never declared capabilities, so those wouldn't work there.
      return externalProvidersService.supportedAccounts.filter(
        (a) => a.features.includes("JoinMeeting") && !a.requiresCapability
      );
    },
  },
  methods: {
    canToggleActivation(provider: ProviderAccountItem): boolean {
      if (this.dontNeedConnection(provider)) {
        return false;
      }
      return provider.provider === "Zoom" || this.isEnabled(provider);
    },
    canShowJoinMeetingSwitch(provider: ProviderAccountItem): boolean {
      if (!provider.features.includes("JoinMeeting")) {
        return false;
      }
      return provider.provider !== "Zoom" || this.isEnabled(provider);
    },
    dontNeedConnection(provider: ProviderAccountItem): boolean {
      return (
        provider.provider === "Mago" ||
        provider.provider === "OpenVisio" ||
        provider.provider === "LiveCollaboration"
      );
    },
    getFeatureText(provider: ProviderAccountItem) {
      if (provider.provider === "LiveCollaboration") {
        return externalProvidersService.translateOr(
          "providers.liveCollaboration.joinMeeting",
          "Join meetings via ID and PIN"
        );
      }
      if (provider.provider === "Google") {
        return this.$t("providers.features.JoinMeetingCode");
      }
      if (provider.provider === "OpenVisio") {
        // OpenVisio joins by meeting ID only — no passcode. The runtime
        // catalogue is replaced by the published Tolgee export, so fall back
        // to the generic label while the key is missing there ($t returns
        // the key itself when unresolved).
        const label = this.$t("providers.features.JoinMeetingId") as string;
        return label === "providers.features.JoinMeetingId"
          ? this.$t("providers.features.JoinMeeting")
          : label;
      }
      return this.$t("providers.features.JoinMeeting");
    },
    getAccount(
      provider: ProviderAccountItem | ExternalProvider
    ): ProviderAccount | undefined {
      const prov = typeof provider === "object" ? provider.provider : provider;
      return this.externalAccounts?.accounts?.find(
        (account: ProviderAccount) => account.provider === prov
      );
    },
    getConnectedEmail(provider: ProviderAccountItem): string {
      return this.getAccount(provider)?.email || "";
    },
    isEnabled(provider: ProviderAccountItem): boolean {
      if (this.dontNeedConnection(provider)) return true;
      return !!this.getAccount(provider)?.email;
    },
    isJoinMeetingEnabled(provider: ProviderAccountItem): boolean {
      const account = this.getAccount(provider);
      if (!account) return false;
      return account.features.some((f) => f.featureType === "JoinMeeting");
    },
    async refreshExternalAccounts() {
      try {
        this.externalAccounts =
          (await externalProvidersService.getWizardExternalAccounts(
            this.deviceConfiguration.deviceConfigurationId,
            this.deviceConfiguration.sessionId
          )) as PodExternalAccountConfiguration;
      } catch (e) {
        const errorCode = (e as any)?.response?.data?.errorCode;
        if (errorCode === "WEA-401" || errorCode === "WEA-402") {
          this.$notification.error(e);
        }
      }
    },
    handleOAuthLogin(provider: ExternalProvider, loginUrl: string) {
      if (externalProvidersService.isSafari()) {
        this.pendingLoginUrl = loginUrl;
        this.pendingProvider = provider;
      } else {
        if (provider === "Zoom") {
          externalProvidersService.openZoomLoginWithLogout(
            loginUrl,
            this.onPopupMeetingClosed
          );
        } else {
          externalProvidersService.openLoginPopup(
            loginUrl,
            this.onPopupMeetingClosed
          );
        }
      }
    },
    async onPopupMeetingClosed() {
      try {
        await this.refreshExternalAccounts();
      } finally {
        this.loading = false;
        this.pendingLoginUrl = null;
        this.pendingProvider = null;
      }
    },
    async onProviderConnect(provider: ExternalProvider) {
      this.loading = true;
      try {
        const featurePayload = {
          provider,
          feature:
            externalProvidersService.getProviderActivationFeature(provider),
          enabled: true,
          config: null,
        } as FeaturePayload;
        await externalProvidersService.wizardToggleFeature(
          featurePayload,
          this.deviceConfiguration.deviceId,
          this.deviceConfiguration.deviceConfigurationId,
          this.deviceConfiguration.sessionId,
          `${window.location.origin}/pub/closeme`
        );
        await this.refreshExternalAccounts();
      } catch (e) {
        const errorCode = (e as any)?.response?.data?.errorCode;
        const loginUrl = (e as any)?.response?.data?.errorData?.loginUrl;
        if (errorCode === "EA401" && loginUrl) {
          this.handleOAuthLogin(provider, loginUrl);
          return;
        }
        this.handleError(e as Error);
      } finally {
        this.loading = false;
      }
    },
    async onProviderDisconnect(provider: ExternalProvider) {
      this.loading = true;
      try {
        const account = this.getAccount(provider);
        if (!account) {
          return;
        }
        await externalProvidersService.wizardDisableProvider(
          account?.podExternalAccountId,
          this.deviceConfiguration.deviceConfigurationId,
          this.deviceConfiguration.sessionId
        );
        await this.refreshExternalAccounts();
      } catch (e) {
        this.handleError(e as Error);
      } finally {
        this.loading = false;
      }
    },
    async onToggleActivation(provider: ProviderAccountItem) {
      if (this.isEnabled(provider)) {
        await this.onProviderDisconnect(provider.provider);
        return;
      }
      await this.onProviderConnect(provider.provider);
    },
    async onJoinMeetingToggle(provider: ExternalProvider, enabled: boolean) {
      this.loading = true;
      try {
        const featurePayload = {
          provider,
          feature: "JoinMeeting",
          enabled,
          config: null,
        } as FeaturePayload;
        await externalProvidersService.wizardToggleFeature(
          featurePayload,
          this.deviceConfiguration.deviceId,
          this.deviceConfiguration.deviceConfigurationId,
          this.deviceConfiguration.sessionId,
          `${window.location.origin}/pub/closeme`
        );
        await this.refreshExternalAccounts();
      } catch (e) {
        this.switchResetKey++;
        const errorCode = (e as any)?.response?.data?.errorCode;
        const loginUrl = (e as any)?.response?.data?.errorData?.loginUrl;
        if (errorCode === "EA401" && loginUrl) {
          this.handleOAuthLogin(provider, loginUrl);
          return;
        }
        this.handleError(e as Error);
      } finally {
        this.loading = false;
      }
    },
    onFinish() {
      const res = this.wizardConfiguration
        ? cloneDeep(this.wizardConfiguration)
        : ({} as WizardConfiguration);
      res.externalAccounts = this.externalAccounts;
      this.$emit("onUpdateConfig", res);
      this.onNext();
    },
  },
  watch: {
    isVisible: {
      immediate: true,
      async handler(visible: boolean) {
        if (!visible || !this.wizardConfiguration?.externalAccounts) {
          return;
        }
        await this.refreshExternalAccounts();
      },
    },
  },
});
